import React, { useEffect } from 'react';
import Layout from '../../components/layout/layout';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';

declare global {
  interface Window {
    RTWidget: any;
    Chatbase: {
      initIframe: (options: {
        chatbotId: string;
        domain: string;
        iframe: HTMLIFrameElement;
      }) => void;
    };
  }
}

export default function OffresPage() {
  const pageUrl = "https://recruteurs.io/candidat/offres";

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://jobs-widget.recruiteecdn.com/widget.js';
    script.async = true;
    script.onload = () => {
      if (window.RTWidget) {
        new window.RTWidget({
          companies: [114974],
          detailsMode: "popup",
          language: "fr",
          themeVars: {
            primary: "#30475e",
            secondary: "#f05454",
            text: "#30475e",
            textDark: "#121317",
            fontFamily: "\"Helvetica Neue\", Helvetica, Arial, \"Lucida Grande\", sans-serif;",
            baseFontSize: "16px"
          },
          flags: {
            showLocation: true,
            showCountry: true,
            showCity: true,
            showState: true,
            showLocationName: true,
            showWorkModel: true,
            groupByLocation: false,
            groupByState: true,
            groupByDepartment: false,
            groupByCompany: false
          }
        });
      }
    };
    document.body.appendChild(script);

    const chatbaseScript = document.createElement('script');
    chatbaseScript.src = "https://www.chatbase.co/embed.min.js";
    chatbaseScript.defer = true;
    chatbaseScript.onload = () => {
      const iframe = document.getElementById('chatbase-iframe') as HTMLIFrameElement;
      if (iframe && window.Chatbase) {
        window.Chatbase.initIframe({
          chatbotId: 'YOUR_CHATBOT_ID_HERE',
          domain: 'www.chatbase.co',
          iframe: iframe
        });
      }
    };
    document.head.appendChild(chatbaseScript);

    return () => {
      document.body.removeChild(script);
      document.head.removeChild(chatbaseScript);
    };
  }, []);

  return (
    <Layout
      title="Offres d'emploi | recruteurs.io"
      description="Découvrez des opportunités de carrière alignées avec vos valeurs. Une expérience de recrutement humaine, claire et respectueuse."
      keywords="offres emploi, recrutement, carrière, job"
    >
      <>
        <WebPageSchema
          page={{
            title: "Offres d'emploi à Marseille | Recruteurs.io",
            description: "Découvrez des opportunités de carrière alignées avec vos valeurs. Une expérience de recrutement humaine, claire et respectueuse.",
            url: pageUrl,
            lastReviewed: "2025-05-22",
            speakable: {
              cssSelector: ".jobs-listing"
            }
          }}
        />

        <BreadcrumbSchema
          items={[
            { name: "Accueil", url: "https://recruteurs.io" },
            { name: "Candidat", url: "https://recruteurs.io/candidat" },
            { name: "Offres", url: pageUrl }
          ]}
        />

        <HeroTemplateCandidats
          title={
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-title font-extrabold leading-tight">
              <span className="block">
                <span className="align-middle mr-2" style={{ color: 'initial' }}>🙌</span>
                <span className="text-black">Le recrutement mérite mieux.</span>
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                Les candidats aussi.
              </span>
            </h1>
          }
          description={
            <>
              <p className="text-neutral-800">
                On ne joue pas avec les gens. On conçoit une expérience de recrutement <strong>humaine, claire, exigeante mais respectueuse</strong> — parce que les talents d'aujourd'hui ne veulent plus être traités comme des CV sur pattes.
              </p>
              <p className="text-neutral-800 mt-4">
                Chez Recruteurs.io, on pense que <strong>chaque entretien, chaque message, chaque onboarding</strong> est une opportunité de montrer qui vous êtes vraiment. Et de construire une équipe qui dure.
              </p>
            </>
          }
          imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offres-emplois-marseille.webp"
          darkOverlay={true}
          cta1={{
            label: "💡 Découvrez notre approche engagée",
            onClick: () => window.location.href = '#approche'
          }}
          cta2={{
            label: "📘 Lire notre manifeste RH",
            href: "/manifeste",
            variant: "outline",
            className: "text-center w-full"
          }}
          page="offres"
          imageStyle={{ maxHeight: '50vh' }}
        />

        <section className="container-custom py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center text-gradient">
              Nos opportunités d'emploi
            </h2>
            <div id="recruitee-careers" className="recruitee-jobs-wrapper" />
          </div>
        </section>
      </>
    </Layout>
  );
}